/**
 * One-click save — extends user_notes via POST /api/engagement/saved (not a second bookmark system).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookmarkIcon, CheckIcon, BellIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const ADVICE_TYPES = new Set([
  'advisor_response',
  'advisor_insight',
  'proposal',
  'strategy',
  'nudge',
  'text',
  'aura_reflection',
  'next_steps',
  'personalized_analysis',
  'routing',
  'routing_response',
  'progress',
]);

export const isAdviceCard = (card) => {
  if (!card) return false;
  const t = card.type;
  if (ADVICE_TYPES.has(t)) return true;
  if (t === 'proposal' && card.proposal) return true;
  return false;
};

const CardSaveButton = ({
  card,
  sourceSurface = 'chat',
  visionId = null,
  onSaved,
  className = '',
  showLabel = false,
}) => {
  const [status, setStatus] = useState('idle');
  const [savedItemId, setSavedItemId] = useState(null);

  if (!isAdviceCard(card)) return null;

  const payloadCard =
    card.type === 'proposal' && card.proposal
      ? { type: 'proposal', ...card, proposal: card.proposal }
      : card;

  const handleSave = async (e) => {
    e.stopPropagation();
    if (status === 'saved') return;
    setStatus('saving');
    try {
      const res = await axios.post('/api/engagement/saved', {
        card: payloadCard,
        source_surface: sourceSurface,
        vision_id: visionId,
      });
      const item = res.data?.saved_item;
      setStatus('saved');
      setSavedItemId(item?.saved_item_id || item?.note_id || null);
      onSaved?.(item);
    } catch (err) {
      console.error('Save failed:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
    }
  };

  const title =
    status === 'saved'
      ? 'Saved to My plan'
      : status === 'error'
        ? 'Could not save'
        : 'Save to My plan';

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <button
        type="button"
        onClick={handleSave}
        disabled={status === 'saving'}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-label transition-colors duration-300 ${
          status === 'saved'
            ? 'border-pine bg-pine-tint text-pine'
            : 'border-hairline text-ink-muted hover:bg-pine-tint hover:text-pine hover:border-pine'
        }`}
        title={title}
      >
        {status === 'saved' ? (
          <CheckIcon className="w-4 h-4" />
        ) : (
          <BookmarkIcon className="w-4 h-4" />
        )}
        {showLabel && (
          <span>{status === 'saved' ? 'Saved' : 'Save'}</span>
        )}
      </button>
      {status === 'saved' && savedItemId && (
        <Link
          to={`/my-plan?saved=${savedItemId}`}
          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-hairline text-label text-pine hover:bg-pine-tint"
        >
          <BellIcon className="w-3.5 h-3.5" />
          Remind me
        </Link>
      )}
    </span>
  );
};

export default CardSaveButton;
